import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  importTableData,
  importTableDataFromFile,
} from "../../../../../api/application/table-data";
import { useAppStore } from "../../../../../store/app.store";
import useGetReference from "../../../../hooks/useGetReference";
import useGetTable from "../../../../hooks/useGetTable";
import { showToast } from "../../../../shared/global-tools/toast-system";
import Icon from "../../../utils/Icons/Icon";
import Spinner from "../../../utils/Spinner/Spinner";
import "./index.scss";

export interface ImportDataStepThreeProps {
  navigateToStep(step: number): void;
}

interface ImportLog {
  timestamp: string;
  message: string;
  type: "info" | "success" | "error" | "warning";
}

export default function ImportDataStepThree({ navigateToStep }: ImportDataStepThreeProps) {
  const { importTableDataInputState, setImportTableDataInputState } = useAppStore();
  const wrapAsync = useWrapAsync();
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importLogs, setImportLogs] = useState<ImportLog[]>([]);
  const [importFinished, setImportFinished] = useState(false);
  const logContainerRef = useRef<HTMLUListElement>(null);
  const { loadingReference, reference } = useGetReference({
    referenceId: importTableDataInputState?.referenceId,
  });
  const { loadingTable, table } = useGetTable({
    referenceId: importTableDataInputState?.referenceId,
    tableId: importTableDataInputState?.tableId,
  });
  const navigate = useNavigate();

  const addLog = (message: string, type: ImportLog["type"] = "info") => {
    const timestamp = new Date().toLocaleTimeString("fr-FR");
    const newLog: ImportLog = { timestamp, message, type };
    setImportLogs((prev) => [...prev, newLog]);
  };

  const clearLogs = () => {
    setImportLogs([]);
    setImportProgress(0);
    setImportFinished(false);
  };

  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [importLogs]);

  const handleImport = wrapAsync(async () => {
    if (
      !importTableDataInputState ||
      (importTableDataInputState.fromFile === undefined &&
        importTableDataInputState.fromAPI === undefined)
    )
      return;

    try {
      setImporting(true);
      clearLogs();

      // Initial log
      addLog("Démarrage de l'importation...", "info");
      setImportProgress(10);

      // Simulate preparation phase
      await new Promise((resolve) => setTimeout(resolve, 1200));
      addLog("Préparation des données...", "info");
      setImportProgress(25);

      // Get total records for progress calculation
      const totalRecords = parsedData ? parsedData.data.length : 0;
      addLog(`${totalRecords} enregistrements à importer`, "info");
      setImportProgress(40);

      let importResponse = { success: false, message: "Erreur non identifiée" };
      // Perform the actual import
      if (importTableDataInputState.fromFile) {
        addLog("Importation depuis le fichier...", "info");
        importResponse = await importTableDataFromFile({
          referenceId: importTableDataInputState.referenceId,
          tableId: importTableDataInputState.tableId,
          input: importTableDataInputState.fromFile,
        });
      } else if (importTableDataInputState.fromAPI) {
        addLog("Importation depuis l'API...", "info");
        importResponse = await importTableData({
          referenceId: importTableDataInputState.referenceId,
          tableId: importTableDataInputState.tableId,
          input: importTableDataInputState.fromAPI,
        });
      }

      setImportProgress(90);
      addLog("Finalisation de l'importation...", "info");

      // Final completion
      await new Promise((resolve) => setTimeout(resolve, 800));
      setImportProgress(100);
      addLog(importResponse.message, "success");
      setImportFinished(true);

      showToast("Importation réussie", {
        severity: "success",
      });
    } catch (error) {
      console.error("Importation error:", error);
      addLog(
        `Erreur lors de l'importation: ${error instanceof Error ? error.message : "Erreur inconnue"}`,
        "error",
      );
      setImportProgress(0);
      showToast("Échec de l'importation", {
        severity: "error",
      });
    } finally {
      setImporting(false);
    }
  });

  const newImport = () => {
    navigateToStep(0);
    setImportTableDataInputState(undefined);
    clearLogs();
  };

  const handleViewData = (referenceId: number, tableId: string) => {
    navigate(
      `/app/databases/${encodeURIComponent(referenceId.toString())}/tables/${encodeURIComponent(tableId)}`,
    );
  };

  if (loadingReference || loadingTable) return <Spinner />;
  const columnsMapping = importTableDataInputState?.fromAPI?.columnsMapping
    ? importTableDataInputState.fromAPI.columnsMapping
    : importTableDataInputState?.fromFile?.columnsMapping
      ? importTableDataInputState.fromFile.columnsMapping
      : undefined;
  const mappedFieldsCount = columnsMapping ? Object.keys(columnsMapping).length : 0;
  const parsedData = importTableDataInputState?.fromFile?.parsedData
    ? importTableDataInputState.fromFile.parsedData
    : importTableDataInputState?.fromAPI?.parsedData
      ? importTableDataInputState.fromAPI.parsedData
      : undefined;
  const totalRecords = parsedData ? parsedData.data.length : 0;
  return (
    <div>
      <div className="ImportDataStepThree-card">
        <h2 className="ImportDataStepThree-title">Exécution de l'importation</h2>

        <div className="ImportDataStepThree-content">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <h3 className="ImportDataStepThree-subtitle">Résumé de l'importation</h3>

              <ul className="ImportDataStepThree-summary">
                <li className="ImportDataStepThree-summaryItem">
                  Référentiel : <strong>{reference?.name}</strong>
                </li>
                <li className="ImportDataStepThree-summaryItem">
                  Table concernée : <strong>{table?.name}</strong>
                </li>
                <li className="ImportDataStepThree-summaryItem">
                  Total à importer : <strong>{totalRecords}</strong>
                </li>
                <li className="ImportDataStepThree-summaryItem">
                  Champs mappés : <strong>{mappedFieldsCount}</strong>
                </li>
              </ul>
            </div>

            {!importFinished && (
              <Button
                className="Btn success icon"
                label="Démarrer l'importation"
                icon={<Icon name="play" width={10} height={10} />}
                onClick={handleImport}
                disabled={importing}
              />
            )}
            {importFinished && reference && table && (
              <Button
                className="Btn primary"
                label="Voir les données"
                onClick={() => handleViewData(reference?.id, table.id)}
              />
            )}
          </div>

          <div style={{ flex: 1, marginLeft: "20px" }}>
            <h3 className="ImportDataStepThree-subtitle">Journal d'importation</h3>

            {importing && (
              <div className="ImportDataStepThree-progress">
                <ProgressBar
                  value={importProgress}
                  className="ImportDataStepThree-progressBar"
                  showValue={false}
                />
                <span className="ImportDataStepThree-progressText">
                  {importProgress}% - En cours d'importation...
                </span>
              </div>
            )}
            <ul className="ImportDataStepThree-log" ref={logContainerRef}>
              {importLogs.length > 0 ? (
                importLogs.map((log, index) => (
                  <li
                    key={`${log.timestamp}-${index}`}
                    className={` ImportDataStepThree-logItem ${log.type}`}
                  >
                    <span className="ImportDataStepThree-logTime">[{log.timestamp}]</span>
                    <span className="ImportDataStepThree-logMessage">{log.message}</span>
                  </li>
                ))
              ) : (
                <li className=" ImportDataStepThree-logItem empty">
                  <span className="ImportDataStepThree-logMessage">
                    Cliquez sur "Démarrer l'importation" pour voir le journal
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      <div className="ImportDataStepThree-footer">
        <Button
          className="Btn secondary outlined"
          label="Retour"
          onClick={() => navigateToStep(2)}
        />
        <Button className="Btn primary" label="Terminer" onClick={newImport} />
      </div>
    </div>
  );
}
